/**
 * Types and defaults for the My Account data hook
 */

export interface AccountData {
	organisation: string;
	abn: string;
	acn?: string;
	accountId: string;
	sector: string;
	services: string;
	adminName: string;
	adminEmail: string;
	adminPhone: string;
	entityType?: 'individual' | 'company' | 'trust' | 'corporate_trust' | 'multi_entity';
	/** AUSTRAC reporting entity type: accounting | legal | real-estate | tsu | other */
	reportingEntityType?: string;
	address?: {
		addressLine1?: string;
		addressLine2?: string;
		city?: string;
		state?: string;
		postcode?: string;
		country?: string;
	} | null;
}

export interface CurrentUser {
	userId?: string;
	email: string;
	firstName?: string;
	lastName?: string;
	role?: string;
	displayName: string;
}

export interface BillingData {
	stripePlanType: string;
	billingCycle: 'monthly' | 'annual';
	paymentMethod: 'card' | 'bank' | 'applepay' | 'googlepay';
	stripeBillingEmail?: string;
	startDate?: string;
	annualAmount?: number;
	monthlyAmount?: number;
	paymentFrequency?: 'monthly' | 'annual';
}

export interface UsageData {
	creditsUsed: number;
	creditsTotal: number;
	creditsRemaining: number;
	creditsReserved?: number;
	cddsThisMonth: number;
	cddsYearToDate: number;
	totalInvitations?: number;
	pendingInvitations?: number;
	acceptedInvitations?: number;
	completed: { count: number; percentage: number };
	inProgress: { count: number; percentage: number };
	failed: { count: number; percentage: number };
}

export interface Invoice {
	/** The payment id. This is what GET_INVOICE is keyed on (AL-326). */
	id: string;
	/**
	 * The invoice number as issued, if one has been. Either system-generated YYMMDDNNN (e.g. 260802001)
	 * or a manually assigned reference (e.g. ARC-2026-0003). Display only — do not gate the download on
	 * it, because a payment has no number until its first invoice is issued. Use taxInvoiceAvailable.
	 */
	invoiceNumber?: string;
	/** True when this payment can be invoiced on request, whether or not it already has been. */
	taxInvoiceAvailable: boolean;
	/** Stripe browser receipt URL, used as a fallback when no tax invoice can be issued. */
	pdfUrl?: string;
	date: string;
	amount: string;
	status: 'paid' | 'pending' | 'failed';
}

export interface LedgerTransaction {
	transactionId: string;
	type: string;
	amount: number;
	balanceAfter: number;
	description: string;
	referenceType: string;
	referenceId: string;
	processedAt: string;
	createdAt: string;
}

export const EMPTY_ACCOUNT: AccountData = {
	organisation: '',
	abn: '',
	accountId: '',
	sector: '',
	services: '',
	adminName: '',
	adminEmail: '',
	adminPhone: '',
	entityType: 'company',
};
